/**
 * welcomeTour — máquina de estados (pura) do tour de boas-vindas do novo usuário.
 *
 * O tour de 1º login TERMINA NO PRIMEIRO ORÇAMENTO:
 *   tutorial (carrossel curto, grava has_seen_onboarding)
 *     → invite ("vamos criar seu primeiro orçamento?")
 *     → quote (coach-marks NO formulário: cliente → itens → salvar) → fim.
 *
 * Por que mudou: antes o tour ia para `nav → profile → companies`, ou seja, seis
 * coach-marks ensinando CONFIGURAÇÕES — inclusive "Indique e ganhe" como 3º
 * passo de quem ainda não tinha feito nada. Nenhum passo ensinava a criar um
 * orçamento, que é o motivo de a pessoa ter baixado o app. Um primeiro contato
 * bom termina com a tarefa feita, não com um tutorial visto.
 *
 * O tour de AJUSTES continua existindo (`nav → profile → companies`), mas agora
 * é SOB DEMANDA — `startSettingsTour()`, oferecido na Central de ajuda. Ele não
 * é a primeira coisa que alguém precisa; é o que se procura quando se procura.
 *
 * A fase é persistida em localStorage para o tour RESUMIR ao cruzar rotas (o
 * driver.js só enxerga o DOM da página atual). A parte visual/driver.js vive no
 * componente WelcomeTour; aqui é só estado — testável sem React.
 */

export const TOUR_PHASES = ['tutorial', 'invite', 'quote', 'nav', 'profile', 'companies'] as const;
export type TourPhase = (typeof TOUR_PHASES)[number];

/**
 * Transições explícitas — NÃO derive do índice do array.
 *
 * São duas cadeias independentes que convivem no mesmo vocabulário: a de
 * boas-vindas (termina em `quote`) e a de ajustes (começa em `nav`). Com
 * `TOUR_PHASES[i + 1]`, terminar o primeiro orçamento emendaria direto no tour
 * de configurações — exatamente o que esta mudança veio desfazer, e sem nada na
 * tela denunciando.
 */
const NEXT_PHASE: Record<TourPhase, TourPhase | null> = {
  tutorial: 'invite',
  invite: 'quote',
  quote: null,
  nav: 'profile',
  profile: 'companies',
  companies: null,
};

const KEY = 'orcamigo_welcome_tour';
/** Evento disparado a cada mudança de fase (o controller re-lê o localStorage). */
export const TOUR_EVENT = 'welcome-tour-changed';

function isPhase(v: string | null): v is TourPhase {
  return v != null && (TOUR_PHASES as readonly string[]).includes(v);
}

export function getTourPhase(): TourPhase | null {
  try {
    const v = localStorage.getItem(KEY);
    return isPhase(v) ? v : null;
  } catch {
    return null;
  }
}

export function setTourPhase(phase: TourPhase): void {
  try {
    localStorage.setItem(KEY, phase);
  } catch {
    /* cota/privado — segue sem persistir */
  }
  window.dispatchEvent(new CustomEvent(TOUR_EVENT));
}

export function clearTour(): void {
  try {
    localStorage.removeItem(KEY);
  } catch {
    /* ignore */
  }
  window.dispatchEvent(new CustomEvent(TOUR_EVENT));
}

/** Inicia o tour de boas-vindas (1º login) — começa pelo tutorial. */
export function startWelcomeTour(): void {
  setTourPhase('tutorial');
}

/**
 * Inicia o tour de AJUSTES sob demanda (Central de ajuda).
 *
 * Separado de `startWelcomeTour` de propósito: quem pede este já usou o app e
 * não quer rever o carrossel de boas-vindas.
 */
export function startSettingsTour(): void {
  setTourPhase('nav');
}

/** Próxima fase ao avançar (botão "Próximo"); null quando a cadeia termina. */
export function nextTourPhase(phase: TourPhase): TourPhase | null {
  return NEXT_PHASE[phase] ?? null;
}

/** Rota alvo de uma fase que exige navegação (null quando não navega sozinha). */
export function tourRouteForPhase(phase: TourPhase): string | null {
  switch (phase) {
    case 'tutorial':
    case 'nav':
      return '/dashboard';
    case 'quote':
      return '/quotes/new';
    case 'profile':
      return '/settings?tab=profile';
    case 'companies':
      return '/settings?tab=companies';
    default:
      return null;
  }
}
